import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { SuiButton, SuiContainer, SuiCollapsible, SuiToggle, SuiTextInput } from '@splashthat/sui';
import styled from 'styled-components';

import {
    getFormSessionStepsSettingsCollapsibleLabel,
    setSessionStepsSettingsCollapsibleLabel,
    getFormSessionStepsSettingsWaitlistLabel,
    setSessionStepsSettingsWaitlistLabel,
    getFormSessionStepsSettingsDescription,
    getFormSessionStepsSettingsSpeakers,
    getFormSessionStepsSettingsTags,
    toggleSessionStepsSettingsDescription,
    toggleSessionStepsSettingsSpeakers,
    toggleSessionStepsSettingsTags,
} from 'common/entities/form/sessionRegistration';
import { Body } from 'common/components/Typography/Body';
import { containerPaddingM } from 'common/styles/variables';
import { getCanUserAccessSessionsTab } from 'common/store/permissions';
import PermissionCheckWrapper from 'utils/PermissionCheckWrapper';
import EditViewSectionContainer from './EditViewSectionContainer';
import SectionSeparator from './components/SectionSeparator';
import { getFormId } from '../../selectors';

const COLLAPSIBLE_HEADER = 'Session Display Options';

const StyledManageSessionText = styled(Body)`
    letter-spacing: 0;
    text-align: center;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
`;

const SessionDisplayOptionsContainer = styled(SuiContainer)`
    gap: 1rem;
    padding: ${containerPaddingM};
    font-weight: 400;
`;

const StyledToggle = styled(SuiToggle)`
    & label {
        font-weight: 400;
    }
`;

const EditSessionsRegistration = () => {
    const dispatch = useDispatch();
    const formId = useSelector(getFormId);
    const collapsibleLabelCopy = useSelector((state) => getFormSessionStepsSettingsCollapsibleLabel(state, formId));
    const waitlistLabelCopy = useSelector((state) => getFormSessionStepsSettingsWaitlistLabel(state, formId));
    const isSpeakersEnabled = useSelector((state) => getFormSessionStepsSettingsSpeakers(state, formId));
    const isDescriptionEnabled = useSelector((state) => getFormSessionStepsSettingsDescription(state, formId));
    const isTagsEnabled = useSelector((state) => getFormSessionStepsSettingsTags(state, formId));
    const canUserAccessSessions = useSelector((state) => getCanUserAccessSessionsTab(state));

    const handleLinkClick = (e) => {
        if (!canUserAccessSessions) {
            e.preventDefault();
        }
    };

    return (
        <React.Fragment>
            <EditViewSectionContainer>
                <SuiContainer variation="flex-group flex-center">
                    <StyledManageSessionText fontWeight={400}>
                        Only sessions with open or waitlist-enabled registration will appear in the form.
                    </StyledManageSessionText>
                    <PermissionCheckWrapper permission={canUserAccessSessions}>
                        {() => (
                            <Link to="/sessions" onClick={handleLinkClick} aria-disabled={!canUserAccessSessions}>
                                <SuiButton
                                    label="Manage Sessions"
                                    variation="secondary"
                                    size="medium"
                                    disabled={!canUserAccessSessions}
                                />
                            </Link>
                        )}
                    </PermissionCheckWrapper>
                </SuiContainer>

                <SectionSeparator />

                <SuiContainer variation="flex-group">
                    <SuiTextInput
                        fullWidth
                        label="Expandable Section Text"
                        value={collapsibleLabelCopy}
                        onChange={(_, newValue) => dispatch(setSessionStepsSettingsCollapsibleLabel(formId, newValue))}
                    />
                </SuiContainer>
                <SuiContainer variation="flex-group">
                    <SuiTextInput
                        fullWidth
                        label="Waitlist Status Badge"
                        value={waitlistLabelCopy}
                        onChange={(_, newValue) => dispatch(setSessionStepsSettingsWaitlistLabel(formId, newValue))}
                    />
                </SuiContainer>
            </EditViewSectionContainer>

            <SuiCollapsible
                fullWidth
                header={COLLAPSIBLE_HEADER}
                content={
                    <SessionDisplayOptionsContainer variation="flex-vertical">
                        <StyledToggle
                            label="Speakers"
                            enabled={isSpeakersEnabled}
                            fullWidth
                            onChange={(_, value) => dispatch(toggleSessionStepsSettingsSpeakers(formId, value))}
                        />
                        <StyledToggle
                            label="Tags"
                            enabled={isTagsEnabled}
                            fullWidth
                            onChange={(_, value) => dispatch(toggleSessionStepsSettingsTags(formId, value))}
                        />
                        <StyledToggle
                            label="Description"
                            enabled={isDescriptionEnabled}
                            fullWidth
                            onChange={(_, value) => dispatch(toggleSessionStepsSettingsDescription(formId, value))}
                        />
                    </SessionDisplayOptionsContainer>
                }
            />
        </React.Fragment>
    );
};

export default EditSessionsRegistration;
